import { Data, DotLottieWorkerReact } from '@lottiefiles/dotlottie-react'
import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BackButton, MainButton, UserLevelProgress, View } from '@ds'
import { Lines, MainLayout, PromoCode, useUserProgress } from '@shared'
import {
  BattlePassLevel,
  useBattlePass,
  useBattlePassActions,
  useBattlePassLevels,
  useBPItem,
  useOverlayActions,
  useShopActions,
  useUser,
} from '@store'

import styles from './BattlePass.module.scss'
import { Border } from './components/Border'
import { Card } from './components/Card'
import { Path } from './components/Path'
import { PathType, PathVariant } from './components/PathType'

enum BattlePassTab {
  Path = 0,
  Leaderboard = 1,
}

const DEFAULT_IMAGE =
  'https://not-platform.fra1.cdn.digitaloceanspaces.com/item/019735ca-1cfd-706b-9b54-00f866cdac72.png'

export const getExtension = (value: string): string | null => {
  const match = value.match(/\.([a-z0-9]+)$/i)
  return match ? match[1] : null
}

const ItemCardContentLottie = ({ imgUrl }: { imgUrl: string }) => {
  const [data, setData] = useState<Data>()

  useEffect(() => {
    fetch(imgUrl)
      .then((res) => res.arrayBuffer())
      .then((data) => setData(data))
  }, [imgUrl])

  return <DotLottieWorkerReact data={data} loop autoplay />
}

const ItemCardContent = ({
  imgUrl,
  rewardType,
}: {
  imgUrl: string
  rewardType?: string
}) => {
  const extension = useMemo(() => getExtension(imgUrl), [imgUrl])

  if (extension === 'lottie') {
    return <ItemCardContentLottie imgUrl={imgUrl} />
  }

  if (extension === 'mp4') {
    return (
      <div className={styles.videoWrapper}>
        <video
          src={imgUrl}
          autoPlay
          loop
          className={classNames(styles.cardVideo, styles.mask)}
        />
      </div>
    )
  }

  return (
    <img
      src={imgUrl || DEFAULT_IMAGE}
      className={classNames(styles.cardImg, {
        [styles.mask]:
          rewardType === 'avatar' || rewardType === 'spiral_effect',
      })}
    />
  )
}

export const BattlePass = () => {
  const navigate = useNavigate()
  const { setOverlayQueue } = useOverlayActions()
  const { t } = useTranslation()
  const progress = useUserProgress()
  const { profile } = useUser()
  const { getBattlePass } = useBattlePassActions()
  const battlePassLevels = useBattlePassLevels()
  const isPremium = profile?.paidBattlepass
  const level = profile?.battlepassLevel ?? 0
  const { getStoreItems } = useShopActions()
  const bpItem = useBPItem()
  const battlePassItems = useBattlePass((state) => state.data?.levels || [])

  const groupedBpItems = useMemo(() => {
    const levels: Record<
      number,
      { default: BattlePassLevel[]; premium: BattlePassLevel[] }
    > = {}

    battlePassItems.forEach((item) => {
      const { level, levelType } = item
      if (!levels[level]) {
        levels[level] = { default: [], premium: [] }
      }
      levels[level][levelType].push(item)
    })

    const renderData: {
      level: number
      default: BattlePassLevel | null
      premium: BattlePassLevel | null
    }[] = []

    Object.entries(levels).forEach(([level, data]) => {
      const maxCount = Math.max(data.default.length, data.premium.length)

      for (let i = 0; i < maxCount; i++) {
        renderData.push({
          level: Number(level),
          default: data.default[i] ? data.default[i] : null,
          premium: data.premium[i] ? data.premium[i] : null,
        })
      }
    })

    renderData.sort((a, b) => a.level - b.level)

    return renderData
  }, [battlePassItems])

  useEffect(() => {
    getBattlePass()
    getStoreItems()
  }, [])

  if (battlePassLevels === null) {
    return null
  }

  const handleCardClick = useCallback((item: BattlePassLevel | null) => {
    if (item) {
      setOverlayQueue([
        {
          type: 'battlePass',
          payload: {
            title: item.name,
            description: item.description || '',
            image: item.iconUrl || '',
            rewardType: item.rewardType,
          },
        },
      ])
    }
  }, [])
  return (
    <MainLayout showInventory={false} isNavbarFadeble>
      {(scrollRef) => (
        <>
          <BackButton onClick={() => navigate('/')} />
          <MainButton hidden />
          <View fadeIn className={styles.view} ref={scrollRef}>
            <div className={styles.container}>
              <Border className={styles.leftBorder} />
              <Border className={styles.rightBorder} />
              <div className={styles.header}>
                <Lines className={styles.lines} />
                <div className={styles.title}>{t('battlePass.starLog')}</div>
                <UserLevelProgress className={styles.userLevel} />
              </div>
              <PromoCode />
              <div>
                <div className={styles.pathsContainer}>
                  <div className={styles.path}>
                    <PathType variant={PathVariant.Free} />
                    <div className={styles.cardsContainer}>
                      {groupedBpItems.map((item, index) => (
                        <Card
                          index={item.default?.id}
                          key={index}
                          withoutDots
                          disabled={item.level > level}
                          title={item.default?.name}
                          onClick={() => handleCardClick(item.default)}
                        >
                          {item.default ? (
                            <ItemCardContent
                              imgUrl={item.default.iconUrl}
                              rewardType={item.default.rewardType}
                            />
                          ) : null}
                        </Card>
                      ))}
                    </div>
                  </div>
                  <div className={styles.pathIndicator}>
                    <Path
                      levelItems={groupedBpItems}
                      level={level}
                      progress={progress}
                    />
                  </div>
                  <div className={styles.path}>
                    <PathType
                      price={bpItem?.prices['XTR'] || 0}
                      onClick={() => {
                        if (!isPremium && bpItem) {
                          setOverlayQueue([
                            {
                              type: 'payment',
                              payload: {
                                item: bpItem,
                              },
                            },
                          ])
                        }
                      }}
                      variant={
                        isPremium
                          ? PathVariant.PremiumActive
                          : PathVariant.PremiumInactive
                      }
                    />
                    <div className={styles.cardsContainer}>
                      {groupedBpItems.map((item, index) => {
                        return (
                          <Card
                            // className={styles.cardStyle}
                            index={index}
                            key={index}
                            locked={!isPremium}
                            disabled={item.level > level}
                            title={item.premium?.name}
                            withoutDots={!item.premium}
                            onClick={() => handleCardClick(item.premium)}
                          >
                            {item.premium ? (
                              <ItemCardContent
                                imgUrl={item.premium.iconUrl}
                                rewardType={item.premium.rewardType}
                              />
                            ) : null}
                          </Card>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </View>
        </>
      )}
    </MainLayout>
  )
}
